import React from 'react';
import './TopBar.css';
import type { UnifiedTopBarProps } from '@frontend/Models/TopBar';
import { useModalManager } from './hooks';
import { renderElements, renderModal } from './utils';

/**
 * UnifiedTopBar - Configurable top navigation bar component
 * 
 * This component provides a flexible, unified approach to top navigation bars
 * across the application. It supports various element types (buttons, toggles,
 * search, dropdowns, modal triggers) and centralized modal management.
 * 
 * @param leftElements - Array of elements to render on the left side
 * @param rightElements - Array of elements to render on the right side
 * @param className - Optional CSS class name (defaults to 'topBar')
 * @param modals - Array of modal configurations
 * @param pageContext - Optional page-specific context data
 * 
 * @example
 * ```tsx
 * const config = {
 *   leftElements: [
 *     { type: 'button', icon: faPlus, onClick: handleCreate, ariaLabel: 'Create' }
 *   ],
 *   rightElements: [
 *     { type: 'search', onSearch: handleSearch, columns: {...}, defaultSelectedColumn: 'Name' }
 *   ],
 *   modals: [
 *     { id: 'export', type: 'export', config: { title: 'Export Data' } }
 *   ]
 * };
 * 
 * return <UnifiedTopBar {...config} />;
 * ```
 */
const UnifiedTopBar: React.FC<UnifiedTopBarProps> = ({
  leftElements = [],
  rightElements = [],
  className = 'topBar',
  modals = [],
  pageContext = {}
}) => {
  // Initialize modal management system
  const { activeModalId, modalData, openModal, closeModal } = useModalManager();

  // Create context for element rendering
  const elementContext = {
    openModal,
    pageContext
  };

  return (
    <>
      {/* Main TopBar Container */}
      <div className={className} data-testid="unified-topbar">
        {/* Left Section - Action buttons, toggles, etc. */}
        <div className="topBar-left" data-testid="topbar-left">
          {renderElements(leftElements, elementContext)}
        </div>
        
        {/* Right Section - Search, export, dropdowns, etc. */}
        <div className="topBar-right" data-testid="topbar-right">
          {renderElements(rightElements, elementContext)}
        </div>
      </div>

      {/* Render active modal if one is open */}
      {activeModalId && renderModal(modals, activeModalId, closeModal, modalData)}
    </>
  );
};

export default UnifiedTopBar;

